import { useState } from "react";
import { Quote, Star } from "lucide-react";
import { testimonials } from "@/data/marketing";

const MAX_LENGTH = 260;

export function TestimonialCards() {
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const toggle = (idx: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });

  return (
    <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto">
      {testimonials.map((t, idx) => {
        const isLong = t.text.length > MAX_LENGTH;
        const isExpanded = expanded.has(idx);
        const displayText = isLong && !isExpanded ? `${t.text.slice(0, MAX_LENGTH).trimEnd()}…` : t.text;

        return (
          <figure
            key={t.name}
            className="relative bg-card border rounded-2xl p-6 flex flex-col transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-xl"
            style={{ boxShadow: "var(--shadow-card)" }}
          >
            <Quote className="h-8 w-8 text-primary/10 absolute top-5 right-5" aria-hidden="true" />
            <div className="flex gap-0.5 mb-3" aria-label="5 out of 5 stars">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-4 w-4 fill-accent text-accent" aria-hidden="true" />
              ))}
            </div>
            <blockquote className="flex-1 mb-4">
              <p className="text-sm text-foreground leading-relaxed whitespace-pre-line">“{displayText}”</p>
              {isLong && (
                <button
                  onClick={() => toggle(idx)}
                  className="text-xs text-primary font-semibold mt-2 hover:underline inline-flex items-center min-h-[44px] py-2"
                >
                  {isExpanded ? "Show less" : "Read more"}
                </button>
              )}
            </blockquote>
            <figcaption className="border-t pt-4 flex items-center gap-3">
              {t.logo ? (
                <img
                  src={t.logo}
                  alt={`${t.company} logo`}
                  loading="lazy"
                  className="w-10 h-10 rounded-full object-contain bg-muted flex-shrink-0"
                />
              ) : (
                <span className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 text-xs font-bold text-primary">
                  {t.initials}
                </span>
              )}
              <span className="min-w-0">
                <span className="block font-bold text-sm truncate">{t.name}</span>
                <span className="block text-xs text-muted-foreground truncate">{t.company}</span>
                <span className="block text-[11px] text-primary font-semibold">{t.role}</span>
              </span>
            </figcaption>
          </figure>
        );
      })}
    </div>
  );
}
